import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Search, ArchiveRestore, History } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const STATUS = [['', 'Todos'], ['Perdido', 'Perdido'], ['Contract Signed', 'Contrato Assinado']];

// Janela de leads arquivados: perdidos (7+ dias) e contratos assinados de meses anteriores.
export const ArchivedLeads = ({ open, onClose, onOpenLead, onChanged }) => {
  const [status, setStatus] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ leads: [], counts: {}, salespeople: [], can_filter_salesperson: false });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/leads-archived', { params: { status, vendedor, q } });
      setData(d);
    } catch { setData({ leads: [], counts: {}, salespeople: [] }); }
    finally { setLoading(false); }
  }, [status, vendedor, q]);
  useEffect(() => { if (open) { const t = setTimeout(load, 250); return () => clearTimeout(t); } }, [open, load]);

  const unarchive = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/unarchive`);
      toast({ title: 'Lead desarquivado', description: `${lead.nome} voltou para o funil` });
      await load();
      onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-5xl" data-testid="crm-archived-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-lg uppercase tracking-tight flex items-center gap-2">
            <Archive className="w-4 h-4 text-primary" /> Leads arquivados
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 border-y border-border py-2.5">
          {STATUS.map(([k, l]) => (
            <button key={k || 'all'} data-testid={`arch-status-${k || 'all'}`} onClick={() => setStatus(k)}
              className={`ent-btn ent-btn-sm ${status === k ? 'ent-btn-primary' : ''}`}>{l}</button>
          ))}
          {data.can_filter_salesperson && (
            <select data-testid="arch-salesperson" className="ent-input w-[190px]" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
              <option value="">Todos os vendedores</option>
              {(data.salespeople || []).map((s) => <option key={s} value={s} className="bg-popover">{s}</option>)}
            </select>
          )}
          <div className="relative ml-auto">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
            <input data-testid="arch-search" className="ent-input pl-8 w-[220px]" placeholder="Nome, telefone, e-mail..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>

        <div className="flex items-center gap-4 text-[11.5px] t-label">
          <span data-testid="arch-count-total">Total: <b className="text-foreground">{data.counts?.total ?? 0}</b></span>
          <span>Perdidos: <b className="t-bad">{data.counts?.lost ?? 0}</b></span>
          <span>Contratos: <b className="t-ok">{data.counts?.signed ?? 0}</b></span>
        </div>

        <div className="max-h-[52vh] overflow-y-auto overflow-x-auto">
          <table className="ent-table" data-testid="arch-table">
            <thead><tr><th>Cliente</th><th>Contato</th><th>Status</th><th>Vendedor</th><th>Arquivado</th><th>Motivo</th><th /></tr></thead>
            <tbody>
              {data.leads.map((l) => (
                <tr key={l.id} data-testid={`arch-row-${l.id}`}>
                  <td className="font-medium max-w-[180px] truncate">{l.nome}<span className="block text-[10px] t-label truncate">{l.interesse || '—'}</span></td>
                  <td className="text-[11px] t-label">{l.telefone || l.email || '—'}</td>
                  <td>
                    <span className={`ent-tag ${l.etapa === 'Contract Signed' ? 't-ok' : 't-bad'}`}>
                      {l.etapa === 'Contract Signed' ? 'Contrato Assinado' : 'Perdido'}
                    </span>
                  </td>
                  <td className="text-[11.5px] max-w-[130px] truncate">{l.vendedor || '—'}</td>
                  <td className="num text-[11px]">{(l.archived_at || '').slice(0, 10)}<span className="block text-[10px] t-label">{l.archived_by}</span></td>
                  <td className="text-[11px] text-foreground/70 max-w-[150px] truncate" title={l.lost_reason || l.archived_reason}>{l.lost_reason || l.archived_reason || '—'}</td>
                  <td className="text-right whitespace-nowrap">
                    <button data-testid={`arch-open-${l.id}`} title="Ver histórico completo" onClick={() => { onOpenLead(l.id); onClose(); }} className="ent-btn ent-btn-sm"><History className="w-3 h-3" /> Histórico</button>
                    <button data-testid={`arch-restore-${l.id}`} title="Desarquivar (volta ao funil)" onClick={() => unarchive(l)} className="ent-btn ent-btn-sm ml-1"><ArchiveRestore className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && !data.leads.length && <p className="ent-empty" data-testid="arch-empty">Nenhum lead arquivado com esses filtros.</p>}
          {loading && <p className="ent-empty">Carregando arquivados...</p>}
        </div>
        <p className="text-[11px] t-label">
          Perdidos são arquivados automaticamente após 7 dias. Contratos assinados ficam no funil durante o mês da
          assinatura. Ao desarquivar, o lead volta ao funil e fica fora do arquivamento automático por 7 dias.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default ArchivedLeads;
